import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from "react-redux";
import OrderStatusCell from "../../../components/dms_common/OrderStatusCell";
import { Close} from '@mui/icons-material';
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
import '../../../features/claimsNew/modals/OpenClaimsDetailsModal.css';
import { truncateText } from "../../../features/stocks/RealTimeStock/RealTimeStockUtils";
import { formatDecimal } from "../../../components/dms_common/DmsUtils";
import CustomCsPcInput from "../../dms_common/CustomCsPcInput";
import { downloadFile} from "../../../features/claimsNew/utils/utils";
import RenderSvg from "../../renderSvg/RenderSvg";
import DownloadModal from './DownloadModal';
import DummyProductPngUrl  from "../../../assets/image/png/dummyProduct.png";
import AssetsSvg from "../../../assets/image/svg/assets.svg";
import OutletSvg from "../../../assets/image/svg/outlet.svg";
import { formatDateToDateMonthYear } from "../../../utils/calenderUtils";
import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer';
import "@cyntler/react-doc-viewer/dist/index.css";
import { DownloadOutlined } from '@ant-design/icons';

interface ViewClaimsDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  accordionData: any;
  updateSelectedRowInRows: (updatedRow: any) => void;
  statusConfig?: any;
  approvalConfig?: any;
  status: string;
}

const ViewClaimsDetailsModal: React.FC<ViewClaimsDetailsModalProps> = ({ 
  isOpen, 
  onClose, 
  accordionData,
  updateSelectedRowInRows,
  statusConfig,
  approvalConfig,
  status
}) => {
  const {selectedViewClaimsRow} = useSelector((state: any) => state.claims);
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const { caseUnitLabel, otherUnitLabel, packUnitLabel, claimTypes, claimSubTypes, currency, batchIdConfiguration, enableFocPurchaseReturnClaims } = ConfigDictionary;
  
  const [expandedBatches, setExpandedBatches] = useState<Record<string, boolean>>({});
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  useEffect(() => {
    setExpandedBatches({});
  }, [selectedViewClaimsRow]);

  useEffect(() => {
    return () => {
      setExpandedBatches({});
    };
  }, []);

  const allBatchesProcessed = useMemo(() => {
    if (accordionData.claimType === claimTypes[0]) {
      return selectedViewClaimsRow?.batchIds?.every(
        (batch: any) => batch?.status
      ) ?? false;
    } else {
      return selectedViewClaimsRow?.invoices?.every(
        (invoice: any) => invoice?.status
      ) ?? false;
    }
  }, [selectedViewClaimsRow, accordionData.claimType]);

  useEffect(() => {
    if (allBatchesProcessed && isOpen) {
      const childItems = accordionData.claimType === claimTypes[0] ? selectedViewClaimsRow.batchIds : selectedViewClaimsRow.invoices;
      const allStatuses = childItems.map((item: any) => item.status);
      let skuStatus = 'PARTIALLY_APPROVED';

      if (allStatuses.every((status: string) => status === 'APPROVED')) {
        skuStatus = 'APPROVED';
      } else if (allStatuses.every((status: string) => status === 'REJECTED')) {
        skuStatus = 'REJECTED';
      }

      if (selectedViewClaimsRow.status !== skuStatus) {
        const updatedCurrentRow = {
          ...selectedViewClaimsRow,
          status: skuStatus,
        };
        updateSelectedRowInRows(updatedCurrentRow);
      }
    }
  }, [allBatchesProcessed, isOpen, selectedViewClaimsRow, updateSelectedRowInRows, accordionData.claimType]);

  const updatedStatusConfig = statusConfig.map((config: any) => ({
    ...config,
    id: config.id?.toUpperCase(),
  }));

  const getSelectedStatus = () => {

    if (enableFocPurchaseReturnClaims && selectedViewClaimsRow.stockType === "foc") {
      const approvedCase = selectedViewClaimsRow.approvedCase || 0;
      const approvedPcs = selectedViewClaimsRow.approvedPcs || 0;
      const approvedOthers = selectedViewClaimsRow.approvedOthers || 0;
      
      const damageCase = selectedViewClaimsRow.damageCase || 0;
      const damagePcs = selectedViewClaimsRow.damagePcs || 0;
      const damageOthers = selectedViewClaimsRow.damageOthers || 0;
      
      if (status === updatedStatusConfig[1].id) { 
        return updatedStatusConfig[1].id; 
      }
      else if (approvedCase === 0 && approvedPcs === 0 && approvedOthers === 0) {
        return updatedStatusConfig[2].id; // REJECTED
      } else if (approvedCase === damageCase && approvedPcs === damagePcs && approvedOthers === damageOthers) {
        return updatedStatusConfig[0].id; // APPROVED
      } else {
        return updatedStatusConfig[3].id; // PARTIALLY_APPROVED
      }
    }

    const approvedAmount = parseFloat(selectedViewClaimsRow.approvedClaimAmount?.replace(/[^\d.]/g, '') || '0');
    const claimAmount = parseFloat(selectedViewClaimsRow.claimAmount?.replace(/[^\d.]/g, '') || '0');
    if (status === updatedStatusConfig[1].id) { 
      return updatedStatusConfig[1].id; }
    else if (approvedAmount === 0) {
      return updatedStatusConfig[2].id;
    } else if (approvedAmount === claimAmount) {
      return updatedStatusConfig[0].id;
    } else if (approvedAmount > 0 && accordionData.claimType === claimTypes[2]) {
      return updatedStatusConfig[0].id;
    }
    else {
      return updatedStatusConfig[3].id;
    }
  }; 
  
  if (!isOpen || !selectedViewClaimsRow) return null;

  const handleClose = () => {
      onClose();
  };  

  const toggleBatchExpand = (batchId: string) => {
    setExpandedBatches(prev => ({
      ...prev,
      [batchId]: !prev[batchId]
    }));
  };

    const damageQty = [
    caseUnitLabel && (
      <span key="cases">
        <span className="open-claims-qty-number">{selectedViewClaimsRow.damageCase}</span>
        <span className="open-claims-qty-unit"> {caseUnitLabel}</span>
      </span>
    ),
    (selectedViewClaimsRow?.otherUnitName || otherUnitLabel) && (
      <span key="others">
      <span className="open-claims-qty-number">{selectedViewClaimsRow.damageOthers}</span>
      <span className="open-claims-qty-unit"> {selectedViewClaimsRow?.otherUnitName || otherUnitLabel}</span>
    </span>
    ),
    packUnitLabel && (
      <span key="pieces">
        <span className="open-claims-qty-number">{selectedViewClaimsRow.damagePcs}</span>
        <span className="open-claims-qty-unit"> {packUnitLabel}</span>
      </span>
    )
  ].filter(Boolean).reduce((acc: React.ReactNode[], element, index, array) => {
    if (index > 0) {
      acc.push(<span key={`comma-${index}`}>, </span>);
    }
    acc.push(element);
    return acc;
  }, []);

  const handleDownloadAll = async () => {
    const files = selectedViewClaimsRow.blobUrl;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileName = file.split('/').pop() ?? 'file';
      await downloadFile(file, fileName);
    }
  };

  const handleOpenDownloadModal = (files: any) => {
    setSelectedFiles(files);
    setIsDownloadModalOpen(true);
  };
  
  const handleCloseDownloadModal = () => {
    setIsDownloadModalOpen(false);
    setSelectedFiles([]);
  };

  const getImageSrc = () => {
    if (accordionData.typeOfExpense === claimSubTypes[4]) {
      return OutletSvg;
    } else if (accordionData.typeOfExpense === claimSubTypes[5]) {
      return AssetsSvg;
    } else {
      return selectedViewClaimsRow?.imgBlobUrl || DummyProductPngUrl;
    }
  };
  
  return (
    <>
      {isDownloadModalOpen && (
        <DownloadModal
          isOpen={isDownloadModalOpen}
          onClose={handleCloseDownloadModal}
          files={selectedFiles}
          outletName={selectedViewClaimsRow.outletName || "N/A"}
        />
      )}
    <div className="open-claims-sku-detail-modal">
      <div className="open-claims-header">
        {accordionData.typeOfExpense === claimSubTypes[2] && (
          <>
            <RenderSvg name="discountIcon" style={{ "height": "30px", "margin-right": "5px" }} />
            <p className="open-claims-remark claims-text-ellipsis" title={selectedViewClaimsRow.remark}>{truncateText(selectedViewClaimsRow.remark,40)}</p>
          </>)}
        <button className="open-claims-close-button" onClick={handleClose}>
          <Close />
        </button>
      </div>

        <div className="open-claims-modal-content">
          <div className="open-claims-sku-info">
            <div className="open-claims-sku-image-container">
              <img src={getImageSrc()} alt={accordionData.claimType === claimTypes[2] ? "Outlet Image" : "SKU Image"} className="open-claims-sku-image" style={accordionData.typeOfExpense === claimSubTypes[4] ? { width: "80%", height: "80%" } : undefined} />
            </div>
            <div className="open-claims-sku-details">
              <h4
                className="open-claims-title claims-text-ellipsis"
                title={accordionData.typeOfExpense === claimSubTypes[4] ? selectedViewClaimsRow?.outletName : accordionData.typeOfExpense === claimSubTypes[5] ? selectedViewClaimsRow?.assetType : selectedViewClaimsRow?.skuName}
              >
                {accordionData.typeOfExpense === claimSubTypes[4]
                  ? selectedViewClaimsRow?.outletName
                  : accordionData.typeOfExpense === claimSubTypes[5]
                    ? selectedViewClaimsRow?.assetType
                    : selectedViewClaimsRow?.skuName}
              </h4>
              <p className="open-claims-sku-code claims-text-ellipsis">{accordionData.typeOfExpense === claimSubTypes[4] ? `Outlet Code: ${selectedViewClaimsRow?.outletCode}` : accordionData.typeOfExpense === claimSubTypes[5] ? `Asset ID: ${selectedViewClaimsRow?.assetId}` : `SKU Code: ${selectedViewClaimsRow?.skuCode}`}{enableFocPurchaseReturnClaims && selectedViewClaimsRow?.stockType === "foc" && (
                <RenderSvg name="focIcon" />
              )}</p>
              {selectedViewClaimsRow.returnOrderNo !== "-" ? <p className="open-claims-sku-code claims-text-ellipsis" title={selectedViewClaimsRow.returnOrderNo}>{selectedViewClaimsRow.returnOrderNo}</p> : <p></p>}
            </div>
          </div>
          {accordionData.claimType === claimTypes[2] ?
            (accordionData.typeOfExpense === claimSubTypes[4] ?
              <div className="open-claims-dates-container">
                <div className="open-claims-date-item">
                  <span className="open-claims-damage-qty-label">Display Start Date</span>
                  <span className="open-claims-display-start-date">
                    {formatDateToDateMonthYear(selectedViewClaimsRow.displayStartDate)}
                  </span>
                </div>
                <div className="open-claims-date-item">
                  <span className="open-claims-damage-qty-label">Display End Date</span>
                  <span className="open-claims-display-end-date">
                    {formatDateToDateMonthYear(selectedViewClaimsRow.displayEndDate)}
                  </span>
                </div>
              </div> : <></>) :
            <div className="open-claims-damage-qty-container">
              <div className="open-claims-damage-qty-content">
                <div className="open-claims-amount-block">
                  <span className="open-claims-damage-qty-label">Total Quantity</span>
                  <span className="open-claims-damage-qty-value">
                    {damageQty.length > 0 ? damageQty : <span className="open-claims-qty-number">0</span>}
                  </span>
                </div>
                {accordionData.claimType === claimTypes[1] ?
                  <div className="open-claims-amount-block">
                    <span className="open-claims-discount-amount-label">Discount Amount</span>
                    <span className="open-claims-discount-amount-value">
                      {selectedViewClaimsRow.claimAmount}
                    </span>
                  </div> : <></>
                }
              </div>
            </div>
          }
          {accordionData.claimType === claimTypes[2] ?
            <>
              <div className="open-claims-attachments-container">
                <div className="open-claims-attachments-header">
                  Attachments
                </div>
                <div className="open-claims-attachments-preview">
                  <DocViewer
                    key={selectedViewClaimsRow.id}
                    documents={[{ uri: selectedViewClaimsRow.blobUrl[0], fileType: selectedViewClaimsRow.blobUrl[0].split('.').pop() ?? 'unknown' }]}
                    initialActiveDocument={{ uri: selectedViewClaimsRow.blobUrl[0] }}
                    pluginRenderers={DocViewerRenderers}
                    config={{
                      header: {
                        disableHeader: true,
                        disableFileName: false,
                        retainURLParams: false,
                      },
                    }}
                    style={{
                      width: '100%',
                      height: '100%',
                      backgroundColor: 'white',
                    }}
                  />
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '8px',
                    alignItems: 'center'
                  }}
                >
                  <button
                    className="circular-icon-wrapper"
                    onClick={() => handleOpenDownloadModal(selectedViewClaimsRow.blobUrl)}
                  >
                    <RenderSvg name="viewIcon" />
                  </button>
                  <button
                    className="circular-icon-wrapper"
                    onClick={handleDownloadAll}
                  >
                    <DownloadOutlined style={{ color: '#02c6b1', fontSize: '18px' }} />
                  </button>
                </div>
              </div>
              {/* {parseFloat(selectedViewClaimsRow.approvedClaimAmount.replace(/[^\d.]/g, '')) === 0 && (
                <div className="open-claims-outlet-container">
                  <div className="open-claims-outlet-info">
                    <div className="open-claims-outlet-label" style={{ color: "#C44C36" }}>Rejection Reason</div>
                    <div className="open-claims-outlet-code">{selectedViewClaimsRow.claimsRejectionReason}</div>
                  </div>
                </div>
              )} */}
            </>
            : <div className="open-claims-batch-section" onWheel={(e) => e.stopPropagation()}>
              <div className="open-claims-batch-header">
                {accordionData.claimType === claimTypes[0] ?
                  <span>{selectedViewClaimsRow.batchIds?.length || 0}{" "}{selectedViewClaimsRow.batchIds?.length === 1 ? "Batch ID" : "Batch IDs"}</span> :
                  accordionData.claimType === claimTypes[1] ?
                    <span>{selectedViewClaimsRow.invoices?.length || 0}{" "}{selectedViewClaimsRow.invoices?.length === 1 ? "Invoice" : "Invoices"}</span> :
                    <></>}
              </div>
              <div className="open-claims-batch-list">
                {accordionData.claimType === claimTypes[0] ? (
                  selectedViewClaimsRow.batchIds?.map((batch: any, index: number) => {
                    const hasRejectedApproval = batch.approvals?.some((approval: any) => approval.status === 'REJECTED');
                    const hasApprovedApproval = batch.approvals?.some((approval: any) => approval.status === 'APPROVED');

                    let borderColor = "#dfe2e7";
                    let backgroundColor = "#FFFFFF";
                    if (!(status === updatedStatusConfig[1].id) && hasRejectedApproval) {
                      borderColor = "#C44C36";
                      backgroundColor = "#FFFAFA";
                    } else if (!(status === updatedStatusConfig[1].id) && hasApprovedApproval) {
                      borderColor = "#00C6B1";
                      backgroundColor = "#FAFFFF";
                    }

                    return (
                      <div key={index} className="open-claims-batch-item">
                        <div className={`open-claims-batch-item-header ${expandedBatches[batch.batchId] ? 'expanded' : ''}`}>
                          <div className="open-claims-batch-info">
                            <div className="open-claims-batch-id claims-text-ellipsis" title={batch.batchId} style={{marginLeft: "8px"}}>
                              {batch.batchId}</div>
                            <div className="open-claims-open-batch-inputs-container">
                              {caseUnitLabel && (
                                <CustomCsPcInput
                                  value={batch.claimableCs}
                                  onChange={() => null}
                                  label={caseUnitLabel}
                                  percentage={85}
                                  borderColor={borderColor}
                                  backgroundColor={backgroundColor}
                                  readOnly={true}
                                />
                              )}
                              {(selectedViewClaimsRow?.otherUnitName || otherUnitLabel) && (
                                <CustomCsPcInput
                                  value={batch.claimableOther}
                                  onChange={() => null}
                                  label={selectedViewClaimsRow?.otherUnitName || otherUnitLabel}
                                  percentage={85}
                                  borderColor={borderColor}
                                  backgroundColor={backgroundColor}
                                  readOnly={true}
                                />
                              )}
                              {packUnitLabel && (
                                <CustomCsPcInput
                                  value={batch.claimablePc}
                                  onChange={() => null}
                                  label={packUnitLabel}
                                  percentage={85}
                                  borderColor={borderColor}
                                  backgroundColor={backgroundColor}
                                  readOnly={true}
                                />
                              )}
                            </div>

                            <div
                              className="open-claims-batch-dropdown"
                              onClick={() => toggleBatchExpand(batch.batchId)}
                            >
                              {expandedBatches[batch.batchId] ? <ExpandLess /> : <ExpandMore />}
                            </div>
                          </div>
                        </div>

                        {expandedBatches[batch.batchId] && (
                          <div className="open-claims-reason-list">
                            {batch.returnReasonArray?.map((reason: any, rIndex: any) => (
                              <div key={rIndex} className="open-claims-reason-item">
                                <div className="open-claims-reason-text claims-text-ellipsis" title={reason.reason}>{reason.reason}</div>
                                <div className="open-claims-reason-qty">
                                  <div className="open-claims-open-batch-inputs-container">
                                    {caseUnitLabel && (
                                      <CustomCsPcInput
                                        value={reason.cs}
                                        onChange={() => null}
                                        label={caseUnitLabel}
                                        percentage={85}
                                        borderColor={borderColor}
                                        backgroundColor={backgroundColor}
                                        readOnly={true}
                                      />
                                    )}
                                    {(selectedViewClaimsRow?.otherUnitName || otherUnitLabel) && (
                                      <CustomCsPcInput
                                        value={reason.other}
                                        onChange={() => null}
                                        label={selectedViewClaimsRow?.otherUnitName || otherUnitLabel}
                                        percentage={85}
                                        borderColor={borderColor}
                                        backgroundColor={backgroundColor}
                                        readOnly={true}
                                      />
                                    )}
                                    {packUnitLabel && (
                                      <CustomCsPcInput
                                        value={reason.pc}
                                        onChange={() => null}
                                        label={packUnitLabel}
                                        percentage={85}
                                        borderColor={borderColor}
                                        backgroundColor={backgroundColor}
                                        readOnly={true}
                                      />
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                            {batch.approvals?.length > 0 && (
                              <div className="open-claims-approval-section">
                                {batch.approvals.some((approval: any) => approval.status !== 'PENDING') && (
                                  <div className="open-claims-batch-id" style={{ fontWeight: 500, marginBottom: "4px" }}>
                                    Approved / Rejected Qty by
                                  </div>
                                )}
                                {batch.approvals
                                  .filter((approval: any) => approval.status !== 'PENDING')
                                  .reduce((acc: any[], approval: any) => {
                                    const existing = acc.find(
                                      (item) =>
                                        item.designation === approval.designation &&
                                        item.userId === approval.userId
                                    );
                                    if (existing) {
                                      const existingQtys = existing.approvedQuantity?.split('.')?.map(Number) || [0, 0, 0];
                                      const newQtys = approval.approvedQuantity?.split('.')?.map(Number) || [0, 0, 0];

                                      existing.approvedQuantity = `${existingQtys[0] + newQtys[0]}.${existingQtys[1] + newQtys[1]}.${existingQtys[2] + newQtys[2]}`;
                                    } else {
                                      acc.push({ ...approval });
                                    }
                                    return acc;
                                  }, []).sort((a: { designation: string }, b: { designation: string }) => {
                                    const aLevel = approvalConfig?.flow.findIndex((level: any) =>
                                      level.includes(a.designation))
                                    const bLevel = approvalConfig?.flow.findIndex((level: any) =>
                                      level.includes(b.designation))
                                    return bLevel - aLevel;
                                  })
                                  .map((approval: any, aIndex: number) => {
                                    const isApproved = approval.status === 'APPROVED';
                                    const isRejected = approval.status === 'REJECTED';
                                    const borderColor = isApproved ? "#00C6B1" : isRejected ? "#C44C36" : "#dfe2e7";
                                    const backgroundColor = isApproved ? "#FAFFFF" : isRejected ? "#FFFAFA" : "#FFFFFF";

                                    return (
                                      <div key={`approval-${aIndex}`} className="open-claims-approval-item">
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                          <div className="open-claims-approval-designation claims-text-ellipsis" title={approval.designation || 'Unknown'}>
                                            {approval.designation || 'Unknown'}
                                          </div>
                                          <div className="open-claims-open-batch-inputs-container">
                                            {caseUnitLabel && (
                                              <CustomCsPcInput
                                                value={batch.claimableCs}
                                                onChange={() => null}
                                                label={caseUnitLabel}
                                                percentage={85}
                                                borderColor={borderColor}
                                                backgroundColor={backgroundColor}
                                                readOnly={true}
                                              />
                                            )}
                                            {(selectedViewClaimsRow?.otherUnitName || otherUnitLabel) && (
                                              <CustomCsPcInput
                                                value={batch.claimableOther}
                                                onChange={() => null}
                                                label={selectedViewClaimsRow?.otherUnitName || otherUnitLabel}
                                                percentage={85}
                                                borderColor={borderColor}
                                                backgroundColor={backgroundColor}
                                                readOnly={true}
                                              />
                                            )}
                                            {packUnitLabel && (
                                              <CustomCsPcInput
                                                value={batch.claimablePc}
                                                onChange={() => null}
                                                label={packUnitLabel}
                                                percentage={85}
                                                borderColor={borderColor}
                                                backgroundColor={backgroundColor}
                                                readOnly={true}
                                              />
                                            )}
                                          </div>
                                        </div>
                                        {approval.status === 'REJECTED' && (
                                          <div className="open-claims-rejection-reason">
                                            <div className='open-claims-rejection-label'>
                                              Rejection Reason </div>
                                            {approval.reason || 'No reason provided'}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })) :
                  (
                    selectedViewClaimsRow.invoices?.map((invoice: any, index: number) => {
                      const hasRejectedApproval = invoice.approvals?.some((approval: any) => approval.status === 'REJECTED');
                      const hasApprovedApproval = invoice.approvals?.some((approval: any) => approval.status === 'APPROVED');

                      let borderColor = "#dfe2e7";
                      let backgroundColor = "#FFFFFF";

                      if (!(status === updatedStatusConfig[1].id) && hasRejectedApproval) {
                        borderColor = "#C44C36";
                        backgroundColor = "#FFFAFA";
                      } else if (!(status === updatedStatusConfig[1].id) && hasApprovedApproval) {
                        borderColor = "#00C6B1";
                        backgroundColor = "#FAFFFF";
                      }
                      const uniqueKey = batchIdConfiguration 
                      ? `${invoice.invoiceNumber}-${invoice.batchIds}` 
                      : invoice.invoiceNumber;
                      return (
                        <div key={index} className="open-claims-batch-item">
                          <div className={`open-claims-batch-item-header ${expandedBatches[uniqueKey] ? 'expanded' : ''}`}>
                            <div className="open-claims-batch-info">
                              <div className="open-claims-batch-id claims-text-ellipsis" title={uniqueKey} style={{ marginLeft: "8px" }}>
                                {batchIdConfiguration
                                  ? `${invoice.invoiceNumber} - ${invoice.batchIds}`
                                  : invoice.invoiceNumber}
                              </div>
                              <div className="open-claims-invoice-amount-container">
                                <div className="open-claims-invoice-amount-box">
                                  <div className="open-claims-invoice-amount-label">Total Amount</div>
                                  <div className="open-claims-invoice-amount-value" style={{ color: "#626262" }}>
                                    {currency} {formatDecimal(invoice.totalAmount)}
                                  </div>
                                </div>
                                <div
                                  className="open-claims-invoice-discount-box"
                                  style={{ borderColor, backgroundColor }}
                                >
                                  <div className="open-claims-invoice-discount-label">Discount Amount</div>
                                  <div className="open-claims-invoice-discount-value">
                                    {currency} {formatDecimal(invoice.discount)}
                                  </div>
                                </div>
                              </div>
                              <div
                                className="open-claims-batch-dropdown"
                                onClick={() => toggleBatchExpand(uniqueKey)}
                              >
                                {expandedBatches[uniqueKey] ? <ExpandLess /> : <ExpandMore />}
                              </div>
                            </div>
                          </div>
                          {expandedBatches[uniqueKey] && (
                            <div className="open-claims-reason-list">
                              <div className="open-claims-outlet-container">
                                <div className="open-claims-outlet-info">
                                  <div className="open-claims-outlet-label">Outlet Name & Code</div>
                                  <div className="open-claims-outlet-name claims-text-ellipsis" title={invoice.outletName}>{invoice.outletName}</div>
                                  <div className="open-claims-outlet-code claims-text-ellipsis" title={invoice.outletCode}>{invoice.outletCode}</div>
                                </div>

                                <div className="open-claims-quantity-section">
                                  <div className="open-claims-total-qty-label">Total Qty</div>
                                  <div className="open-claims-outlet-quantities">
                                    {caseUnitLabel && (
                                      <CustomCsPcInput
                                        value={invoice.cases}
                                        onChange={() => null}
                                        label={caseUnitLabel}
                                        percentage={75}
                                        borderColor={borderColor}
                                        backgroundColor={backgroundColor}
                                        readOnly={true}
                                      />
                                    )}
                                    {(selectedViewClaimsRow?.otherUnitName || otherUnitLabel) && (
                                      <CustomCsPcInput
                                        value={invoice.others}
                                        onChange={() => null}
                                        label={selectedViewClaimsRow?.otherUnitName || otherUnitLabel}
                                        percentage={75}
                                        borderColor={borderColor}
                                        backgroundColor={backgroundColor}
                                        readOnly={true}
                                      />
                                    )}
                                    {packUnitLabel && (
                                      <CustomCsPcInput
                                        value={invoice.pieces}
                                        onChange={() => null}
                                        label={packUnitLabel}
                                        percentage={75}
                                        borderColor={borderColor}
                                        backgroundColor={backgroundColor}
                                        readOnly={true}
                                      />
                                    )}
                                  </div>
                                </div>
                              </div>
                              {invoice.approvals?.length > 0 && (
                                <div className="open-claims-approval-section">
                                  {invoice.approvals.some((approval: any) => approval.status !== 'PENDING') && (
                                    <div className="open-claims-batch-id" style={{ fontWeight: 500, marginBottom: "4px" }}>
                                      Approved / Rejected Amt by
                                    </div>
                                  )}
                                  {invoice.approvals
                                    .filter((approval: any) => approval.status !== 'PENDING')
                                    .reduce((acc: any[], approval: any) => {
                                      const existing = acc.find(
                                        (item) =>
                                          item.designation === approval.designation &&
                                          item.userId === approval.userId
                                      );
                                      if (existing) {
                                        existing.approvedAmount = formatDecimal(parseFloat(existing.approvedAmount) + parseFloat(approval.approvedAmount));
                                      } else {
                                        acc.push({ ...approval });
                                      }
                                      return acc;
                                    }, []).sort((a: { designation: string }, b: { designation: string }) => {
                                      const aLevel = approvalConfig?.flow.findIndex((level: any) =>
                                        level.includes(a.designation))
                                      const bLevel = approvalConfig?.flow.findIndex((level: any) =>
                                        level.includes(b.designation))
                                      return bLevel - aLevel;
                                    })
                                    .map((approval: any, aIndex: number) => {
                                      const isApproved = approval.status === 'APPROVED';
                                      const isRejected = approval.status === 'REJECTED';
                                      const borderColor = isApproved ? "#00C6B1" : isRejected ? "#C44C36" : "#dfe2e7";

                                      return (
                                        <div key={`approval-${aIndex}`} className="open-claims-approval-item">
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'space-between' }}>
                                            <div className="open-claims-approval-designation claims-text-ellipsis" title={approval.designation || 'Unknown'}>
                                              {approval.designation || 'Unknown'}
                                            </div>
                                            <div className="open-claims-invoice-discount-box"
                                              style={{ borderColor, backgroundColor: isApproved ? "#FAFFFF" : isRejected ? "#FFFAFA" : "#FFFFFF" }}>
                                              <div className="open-claims-invoice-discount-label">Discount Amount</div>
                                              <div className="open-claims-invoice-discount-value">
                                                {currency} {formatDecimal(invoice.discount)}
                                              </div>
                                            </div>
                                          </div>
                                          {approval.status === 'REJECTED' && (
                                            <div className="open-claims-rejection-reason">
                                              <div className='open-claims-rejection-label'>
                                                Rejection Reason </div>
                                              {approval.reason || 'No reason provided'}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )
                    })
                  )
                }
              </div>
            </div>}
        </div>

      <div className="open-claims-modal-footer">
        <div className="open-claims-footer-content">
            <>
              <div className="open-claims-claim-amount-container">
                <span className="open-claims-claim-amount-label">Approved Claim Amount</span>
                <span className="open-claims-claim-amount-value">
                  {selectedViewClaimsRow.approvedClaimAmount}
                </span>
              </div>
              <div className="open-claims-claim-amount-container">
                {statusConfig && (
                  <OrderStatusCell orderStatusConfig={statusConfig} statusId={getSelectedStatus()} />
                )}
              </div>
            </>
        </div>
      </div>
    </div>
    </>
  );
};

export default ViewClaimsDetailsModal;